import axiosInstance from './axiosConfig';
import { isOnline } from './offlineQueue';
import { toast } from 'react-toastify';
import { BASE_URL, getCurrentLanguage, getToken } from './utils';

/**
 * ULTRA-DEEP Offline Prefetcher (v7 - Full Route Coverage)
 * Mirroring all 49 Slices with nested sub-requests, tree crawling, and hidden routes.
 */

const MAIN_ENDPOINTS = [
  '/customers', '/vendors', '/projects', '/contracts', '/employees',
  '/attendance', '/salaries', '/vacations', '/leave-requests', '/payslips',
  '/shifts', '/job-titles', '/departments', '/contractors', '/engineers',
  '/journal-entries', '/receipt-vouchers', '/payment-vouchers',
  '/notes-receivable', '/notes-payable', '/taxes', '/opening-balance',
  '/financial-periods', '/currencies', '/exchange-rates', '/banks',
  '/safes', '/equipment', '/tasks', '/progress-bills', '/lab-tests',
  '/quotations', '/units', '/roles', '/permissions', '/users', '/audit-logs',
  '/tests', '/leave-types', '/positions', '/work-shifts', '/equipment/expenses',
  '/accounts/posting', '/project-tests', '/penalty-types' // Added penalty-types
];

const META_ENDPOINTS = [
  '/accounts/tree', '/accounts/unlinked', '/account-links',
  '/cost-centers/tree', '/accounts/banks-and-safes', '/user', '/profile',
  '/exchange-rates/effective', '/hr-reports/working-hours',
  '/reports/projects/quotations', '/reports/projects/contracts',
  '/reports/projects/progress-bills', '/reports/projects/tasks',
  '/reports/projects/overview', '/accounting-reports/income-statement',
  '/accounting-reports/balance-sheet', '/accounting-reports/cash-flow-statement',
  '/accounting-reports/customer-aging', '/accounting-reports/vendor-aging',
  '/accounting-reports/general-ledger', '/accounting-reports/cost-center-report',
  '/accounting-reports/trial-balance', '/audit-logs/statistics', 
  '/attendance/reports', '/payroll-reports/summary', '/working-hours-reports/summary'
];

const DEEP_PREFETCH_MAP = [
  { path: '/customers', detail: (id) => `/customers/${id}`, subs: [(id) => `/accounts/${id}/statement`] },
  { path: '/vendors', detail: (id) => `/vendors/${id}` },
  { path: '/projects', detail: (id) => `/projects/${id}`, subs: [(id) => `/reports/projects/${id}/summary`, (id) => `/projects/${id}/team`] },
  { path: '/contracts', detail: (id) => `/contracts/${id}` },
  { path: '/progress-bills', detail: (id) => `/progress-bills/${id}`, subs: [(id) => `/progress-bills/${id}/journal-entries`] },
  { path: '/accounts', detail: (id) => `/accounts/${id}`, subs: [(id) => `/accounts/${id}/statement`] },
  { path: '/journal-entries', detail: (id) => `/journal-entries/${id}` },
  { path: '/tasks', detail: (id) => `/tasks/${id}` },
  { path: '/employees', detail: (id) => `/employees/${id}`, subs: [(id) => `/employees/${id}/salary`, (id) => `/employees/${id}/attendance`] },
  { path: '/equipment', detail: (id) => `/equipment/${id}`, subs: [(id) => `/equipment/${id}/expenses`] },
  { path: '/quotations', detail: (id) => `/quotations/${id}` },
  { path: '/users', detail: (id) => `/users/${id}` },
  { path: '/contractors', detail: (id) => `/contractors/${id}` },
  { path: '/receipt-vouchers', detail: (id) => `/receipt-vouchers/${id}` },
  { path: '/payment-vouchers', detail: (id) => `/payment-vouchers/${id}` },
  { path: '/opening-balance', detail: (id) => `/opening-balance/${id}` },
  { path: '/banks', detail: (id) => `/banks/${id}` },
  { path: '/safes', detail: (id) => `/safes/${id}` },
  { path: '/notes-receivable', detail: (id) => `/notes-receivable/${id}` },
  { path: '/notes-payable', detail: (id) => `/notes-payable/${id}` },
  { path: '/taxes', detail: (id) => `/taxes/${id}` },
  { path: '/currencies', detail: (id) => `/currencies/${id}` },
  { path: '/financial-periods', detail: (id) => `/financial-periods/${id}` },
  { path: '/lab-tests', detail: (id) => `/lab-tests/${id}` },
  { path: '/units', detail: (id) => `/units/${id}` },
  { path: '/departments', detail: (id) => `/departments/${id}` },
  { path: '/positions', detail: (id) => `/positions/${id}` },
  { path: '/work-shifts', detail: (id) => `/work-shifts/${id}` },
  { path: '/job-titles', detail: (id) => `/job-titles/${id}` },
  { path: '/leave-types', detail: (id) => `/leave-types/${id}` },
  { path: '/leave-requests', detail: (id) => `/leave-requests/${id}` },
  { path: '/attendance', detail: (id) => `/attendance/${id}` },
  { path: '/salaries', detail: (id) => `/salaries/${id}` },
  { path: '/payslips', detail: (id) => `/payslips/${id}` },
  { path: '/penalty-types', detail: (id) => `/penalty-types/${id}` }
];

const DROPDOWNS = [
  { url: '/accounts/tree', params: {} },
  { url: '/cost-centers', params: {} },
  { url: '/customers', params: {} },
  { url: '/vendors', params: {} },
  { url: '/projects', params: {} },
  { url: '/employees', params: {} },
  { url: '/contracts', params: {} },
  { url: '/contractors', params: {} },
  { url: '/engineers', params: {} },
  { url: '/tests', params: {} },
  { url: '/lab-tests', params: {} },
  { url: '/departments', params: {} },
  { url: '/positions', params: {} },
  { url: '/work-shifts', params: {} },
  { url: '/roles', params: {} },
  { url: '/permissions', params: {} },
  { url: '/users', params: {} },
  { url: '/job-titles', params: {} },
  { url: '/leave-types', params: {} },
  { url: '/units', params: {} },
  { url: '/banks', params: {} },
  { url: '/safes', params: {} },
  { url: '/currencies', params: {} },
  { url: '/taxes', params: {} },
  { url: '/financial-periods', params: {} },
  { url: '/equipment', params: {} },
  { url: '/penalty-types', params: {} }
];

let isPrefetching = false;

// Helper to crawl a tree and extract ALL IDs recursively (no limit)
const extractIdsFromTree = (nodes, limit = null) => {
  const ids = [];
  const traverse = (list) => {
    if (!list || !Array.isArray(list)) return;
    for (const node of list) {
      if (node.id) {
        ids.push(node.id);
        // If limit is set and reached, stop (but for accounts tree, we want all)
        if (limit !== null && ids.length >= limit) break;
      }
      // Recursively traverse children
      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        traverse(node.children);
      }
    }
  };
  traverse(nodes);
  return ids;
};

export const prewarmOfflineCache = async (delayMs = 0) => {
  const token = getToken();
  if (!token || isPrefetching || !isOnline()) return;
  if (localStorage.getItem('offline_prewarm_done') === 'true') return;

  if (delayMs > 0) {
    await new Promise(r => setTimeout(r, delayMs));
    if (!getToken() || isPrefetching || localStorage.getItem('offline_prewarm_done') === 'true') return;
  }
  
  isPrefetching = true;
  const isRTL = getCurrentLanguage() === 'ar';
  
  const toastId = toast.info(
    isRTL ? 'جاري المزامنة الشاملة للنظام...' : 'Total System Synchronization...',
    { autoClose: false, closeOnClick: false, draggable: false, progress: 0 }
  );

  const itemsPerDeep = 5;
  
  // Use dynamic progress calculation instead of fixed totalTasks
  // This ensures progress always reaches 100% even if actual tasks differ
  let completedTasks = 0;
  let estimatedTotalTasks = 
    META_ENDPOINTS.length + 
    (DROPDOWNS.length * 3) + // Each dropdown: fetchAllWithTotal may need 2-3 calls
    (MAIN_ENDPOINTS.length * 3) + // Pagination variations (10, 15, 10-p2)
    (MAIN_ENDPOINTS.length * 3) + // Each endpoint: fetchAllWithTotal (may need 2-3 calls)
    (DEEP_PREFETCH_MAP.length * itemsPerDeep) +
    50; // Buffer for tree crawling and other operations

  let lastReportedProgress = 0;
  const updateProgress = (increment = 1) => {
    completedTasks += increment;
    
    let calculatedProgress;
    if (completedTasks < estimatedTotalTasks * 0.9) {
      calculatedProgress = completedTasks / estimatedTotalTasks;
    } else {
      // 90% to 99% Zone: Use an asymptotic approach so it never stops but slows down
      // Each new task moves the bar slightly closer to 99%
      const overflow = completedTasks - (estimatedTotalTasks * 0.9);
      // The growthFactor controls how slow the progress moves in the final stage
      // A value of 300-500 is good for large datasets
      const growthFactor = 500; 
      calculatedProgress = 0.9 + (0.09 * (1 - Math.exp(-overflow / growthFactor)));
    }
    
    // Ensure progress is monotonic and ALWAYS moves at least a tiny bit (0.01%)
    // to give the user a visual sense that work is still happening
    const progress = Math.min(Math.max(lastReportedProgress + 0.0001, calculatedProgress), 0.99);
    lastReportedProgress = progress;

    toast.update(toastId, {
      progress: progress,
      render: isRTL 
        ? `جاري المزامنة الشاملة... (${Math.round(progress * 100)}%)` 
        : `Total Sync... (${Math.round(progress * 100)}%)`
    });
  };

  const safeGet = async (url, params = {}, shouldUpdateProgress = false) => {
    try {
      if (!url) {
        if (shouldUpdateProgress) updateProgress();
        return;
      }
      const fullUrl = `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );
      
      await Promise.race([
        axiosInstance.get(fullUrl, { params }),
        timeoutPromise
      ]);
      await new Promise(r => setTimeout(r, 10)); 
      if (shouldUpdateProgress) updateProgress();
    } catch (e) {
      console.warn(`[OfflineSync] Skipped: ${url}`, e.message);
      // Still update progress even on error to keep sync moving
      if (shouldUpdateProgress) updateProgress();
    }
  };

  // Helper to get total count and fetch all data dynamically
  const fetchAllWithTotal = async (url, params = {}, shouldUpdateProgress = true) => {
    try {
      // First, get total count with small per_page
      const firstPageParams = { ...params, page: 1, per_page: 1 };
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );
      
      const firstRes = await Promise.race([
        axiosInstance.get(`${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`, { params: firstPageParams }),
        timeoutPromise
      ]);
      if (shouldUpdateProgress) updateProgress();
      
      // Extract total from response
      let total = 0;
      if (firstRes.data?.meta?.total) {
        total = firstRes.data.meta.total;
      } else if (firstRes.data?.data?.meta?.total) {
        total = firstRes.data.data.meta.total;
      } else if (firstRes.data?.pagination?.total) {
        total = firstRes.data.pagination.total;
      } else if (Array.isArray(firstRes.data?.data)) {
        // If it's an array, we might need to check if there's a meta elsewhere
        total = firstRes.data.meta?.total || firstRes.data.total || 0;
      } else if (Array.isArray(firstRes.data)) {
        // If response is directly an array
        total = firstRes.data.length;
      }
      
      // If total is 0 or not found, try with per_page: 10 to get actual total
      if (total === 0) {
        try {
          const secondPageParams = { ...params, page: 1, per_page: 10 };
          const secondRes = await Promise.race([
            axiosInstance.get(`${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`, { params: secondPageParams }),
            timeoutPromise
          ]);
          if (shouldUpdateProgress) updateProgress();
          if (secondRes.data?.meta?.total) {
            total = secondRes.data.meta.total;
          } else if (secondRes.data?.data?.meta?.total) {
            total = secondRes.data.data.meta.total;
          } else if (secondRes.data?.pagination?.total) {
            total = secondRes.data.pagination.total;
          } else if (Array.isArray(secondRes.data?.data)) {
            total = secondRes.data.meta?.total || secondRes.data.total || secondRes.data.data.length || 0;
          } else if (Array.isArray(secondRes.data)) {
            total = secondRes.data.length;
          } else {
            total = secondRes.data.meta?.total || secondRes.data?.length || 0;
          }
        } catch (secondError) {
          console.warn(`[OfflineSync] Error getting total for ${url}, using fallback:`, secondError.message);
          if (shouldUpdateProgress) updateProgress();
        }
      }
      
      // Now fetch all data with per_page = total (or use 10000 as fallback if total is too large)
      if (total > 0) {
        // For tree endpoints, we might need to fetch without pagination
        if (url.includes('/tree')) {
          // Tree endpoints usually return all data without pagination
          await safeGet(url, params, shouldUpdateProgress);
        } else {
          // For regular endpoints, fetch with per_page = total
          const allDataParams = { ...params, page: 1, per_page: Math.min(total, 10000) }; // Cap at 10000 for safety
          await safeGet(url, allDataParams, shouldUpdateProgress);
          
          // If total > 10000, we need to fetch in batches
          if (total > 10000) {
            const batches = Math.ceil(total / 10000);
            for (let page = 2; page <= batches; page++) {
              const batchParams = { ...params, page, per_page: 10000 };
              await safeGet(url, batchParams, shouldUpdateProgress);
            }
          }
        }
      } else {
        // Fallback: use 1000 if total not found
        await safeGet(url, { ...params, page: 1, per_page: 1000 }, shouldUpdateProgress);
      }
    } catch (e) {
      console.warn(`[OfflineSync] Error fetching total for ${url}, using fallback:`, e.message);
      // Fallback: use 1000 if error
      await safeGet(url, { ...params, page: 1, per_page: 1000 }, shouldUpdateProgress);
    }
  };

  try {
    // Stage 1: Meta & Trees
    for (const ep of META_ENDPOINTS) {
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 30000)
        );
        const fullUrl = `${BASE_URL}${ep.startsWith('/') ? '' : '/'}${ep}`;
        const res = await Promise.race([
          axiosInstance.get(fullUrl),
          timeoutPromise
        ]);
        
        if (ep.includes('tree') && res.data?.data) {
          const treeData = Array.isArray(res.data.data) ? res.data.data : (res.data.data.data || []);
          const pathPrefix = ep.includes('accounts') ? '/accounts' : '/cost-centers';
          
          // For accounts tree, extract ALL IDs (no limit) to ensure complete sync
          // For cost centers, also extract all IDs
          const allIds = extractIdsFromTree(treeData, null); // null = no limit, get all
          
          
          // Fetch all account/cost center details
          for (const id of allIds) {
            await safeGet(`${pathPrefix}/${id}`, {}, true);
            if (pathPrefix === '/accounts') {
              // Also fetch statement for each account
              await safeGet(`${pathPrefix}/${id}/statement`, {}, true);
            }
          }
          
          // Update progress estimation based on actual tree size
          if (allIds.length > 10) {
            // Adjust estimated tasks if tree is larger than expected
            const additionalTasks = (allIds.length - 10) * (pathPrefix === '/accounts' ? 2 : 1);
            estimatedTotalTasks += additionalTasks;
          }
        }
      } catch (e) {
        console.warn(`[OfflineSync] Error in META endpoint ${ep}:`, e.message);
      } finally {
        updateProgress();
      }
    }
    
    // Manual pre-fetch for Posting Accounts (Add Journal Entry Dropdown)
    try {
      await safeGet('/accounts/posting', { page: 1, per_page: 10000 }, true);
    } catch (e) {
      console.warn('[OfflineSync] Error fetching posting accounts:', e);
    }
    
    // Stage 2: Large Dropdowns (Fetch all items dynamically based on total count)
    for (const d of DROPDOWNS) {
      await fetchAllWithTotal(d.url, d.params, true);
    }

    // Stage 3: Paginated Lists & Deep Details
    const paginations = [
      { per_page: 10, page: 1 }, 
      { per_page: 15, page: 1 }, 
      { per_page: 10, page: 2 }
    ];

    for (const endpoint of MAIN_ENDPOINTS) {
      try {
        // First, do pagination variations for initial load
        for (const params of paginations) {
          try {
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Request timeout')), 30000)
            );
            const res = await Promise.race([
              axiosInstance.get(`${BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`, { params }),
              timeoutPromise
            ]);
            
            if (params.page === 1 && params.per_page === 10) {
              const deepMapping = DEEP_PREFETCH_MAP.find(d => endpoint.includes(d.path));
              if (deepMapping && res.data?.data) {
                const dataRoot = res.data.data;
                const items = Array.isArray(dataRoot) ? dataRoot : (dataRoot.data || []);
                
                for (let i = 0; i < Math.min(itemsPerDeep, items.length); i++) {
                  const item = items[i];
                  if (item?.id) {
                    await safeGet(deepMapping.detail(item.id), {}, true);
                    if (deepMapping.subs) {
                      for (const subFunc of deepMapping.subs) {
                        await safeGet(subFunc(item.id), {}, true);
                      }
                    }
                  }
                }
                // Update progress for any remaining items that weren't processed
                for (let i = items.length; i < itemsPerDeep; i++) {
                  updateProgress();
                }
              } else if (deepMapping) {
                for (let i = 0; i < itemsPerDeep; i++) updateProgress();
              }
            }
          } catch (e) {
            console.warn(`[OfflineSync] Error in pagination for ${endpoint}:`, e.message);
          } finally {
            updateProgress();
          }
        }
        
        // Then, fetch all data dynamically based on total
        await fetchAllWithTotal(endpoint, {}, true);
      } catch (e) {
        console.warn(`[OfflineSync] Error processing endpoint ${endpoint}:`, e.message);
        // Still update progress to keep sync moving
        updateProgress(3); // Account for the 3 pagination calls that might have been skipped
        updateProgress(3); // Account for fetchAllWithTotal that might have been skipped
      }
    }

    // Ensure progress reaches 100%
    completedTasks = estimatedTotalTasks;
    updateProgress(0); // Force update to 100%
    
    localStorage.setItem('offline_prewarm_done', 'true');
    toast.update(toastId, {
      render: isRTL ? 'النظام جاهز للعمل أوفلاين بنسبة 100% ✅' : 'System is 100% ready offline ✅',
      type: 'success',
      autoClose: 4000,
      progress: 1
    });

  } catch (err) {
    console.error('[OfflineSync] Fatal Failure:', err);
    toast.dismiss(toastId);
  } finally {
    isPrefetching = false;
  }
};
